import { legendConfigApi, type RequestType } from 'api-validations/stitch'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { type ApiClient, toQuery, useApiClient, useQuery } from 'shared-ui/api-provider'

import { useStitchRequestType } from '../../field-data'
import { useMapContext } from '../FieldMap/MapProvider'
import { useAsAppliedLayerOrNull } from '../map-state-store'
import type { LegendState } from './useUserFacingLegend'

/**
 * Get the default legend configuration for the current layer.
 * Do not use for displaying the user-facing legend (which is editable),
 * use `useUserFacingLegendConfig` instead.
 *
 * When the pane holds a non-as-applied selection (a plan asset or the plan
 * editor) there is no layer to describe — the query is disabled and `data`
 * stays undefined.
 */
export const useDefaultLegendConfig = (overrideStreamId?: string) => {
  const orgCode = useOrgCodeOrThrow()
  const { map } = useMapContext()
  const layer = useAsAppliedLayerOrNull(map.isComparison())
  const apiClient = useApiClient()
  const requestType = useStitchRequestType()
  const streamId = overrideStreamId ?? layer?.streamId ?? ''

  return useQuery({
    ...queryConfig(
      {
        orgCode,
        streamId,
        applicationId: layer?.applicationId ?? '',
        layerId: layer?.layerId ?? '',
        requestType
      },
      apiClient
    ),
    enabled: layer !== null
  })
}

const queryConfig = (params: Params, apiClient: ApiClient) =>
  toQuery({
    queryKey: ['layer-metadata', params],
    queryFn: async () => {
      const path = legendConfigApi.pathFor({
        orgCode: params.orgCode,
        streamId: params.streamId,
        applicationId: params.applicationId,
        layerId: params.layerId,
        requestType: params.requestType
      })
      const response = await apiClient.get(path)
      const data = legendConfigApi.responseSchema.parse(response.body)
      const isContinuous = !data.discrete
      const discreteColors = data.legend.colors.filter((c) => c.type === 'discrete')

      const initialRange = {
        low: isContinuous ? data.legend.range.minimum : Number.NaN,
        high: isContinuous ? data.legend.range.maximum : Number.NaN
      }

      return {
        initialRange,
        activeRange: initialRange,
        definedRange: {
          low: isContinuous ? data.defined_range.minimum : Number.NaN,
          high: isContinuous ? data.defined_range.maximum : Number.NaN
        },
        unit: data.units,
        descending: data.discrete ? false : data.legend.descending,
        continuousBinCount: data.legend.colors.length - discreteColors.length,
        adjustmentFactor: isContinuous ? data.gain : undefined,
        negated: data.negated ?? false,
        isHighlightMode: false,
        highlightLabel: undefined,
        editableRange: {
          low: isContinuous
            ? Math.min(data.populated_range.minimum, data.legend.range.minimum)
            : Number.NaN,
          high: isContinuous
            ? Math.max(data.populated_range.maximum, data.legend.range.maximum)
            : Number.NaN
        },
        normalizedGradient: isContinuous
          ? data.gradient.filter((s) => s.type === ('continuous' as const))
          : [],
        shader: data.shader,
        discreteColors,
        isDiscrete: data.discrete,
        name: data.name
      } satisfies LegendState
    }
  })

type Params = {
  orgCode: string
  streamId: string
  applicationId: string
  layerId: string
  requestType: RequestType
}
